/**
 * "Watch On" rail — branded platform pills, content filters, and a See All link.
 *
 * Pills show real TMDB CDN platform logos (pre-fetched from /api/watch-on-providers).
 * Active pill gets a glowing branded ring; inactive pills show a subtle dark variant.
 * Filters (type, sort, language, rating) are applied client-side for instant response.
 * Selection persists via localStorage. Data shuffled server-side on every request.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Tv2, ArrowRight, Layers } from "lucide-react";
import { Link } from "react-router-dom";
import MovieCard from "./MovieCard";
import { api } from "../lib/api";
import { PLATFORM_STYLES } from "./PlatformChip";
import { readSwrCache, writeSwrCache } from "../lib/swrCache";

const PILL_ORDER = [
  { key: "netflix",  name: "Netflix",          ring: "ring-red-500" },
  { key: "prime",    name: "Prime Video",      ring: "ring-sky-500" },
  { key: "hotstar",  name: "JioHotstar",       ring: "ring-blue-500" },
  { key: "sonyliv",  name: "Sony LIV",         ring: "ring-cyan-500" },
  { key: "zee5",     name: "ZEE5",             ring: "ring-violet-500" },
  { key: "apple",    name: "Apple TV+",        ring: "ring-neutral-300" },
  { key: "mx",       name: "Amazon MX Player", ring: "ring-orange-500" },
];

const LANG_OPTIONS = [
  { value: "all",   label: "All" },
  { value: "hi",    label: "Hindi" },
  { value: "ta",    label: "Tamil" },
  { value: "te",    label: "Telugu" },
  { value: "ml",    label: "Malayalam" },
  { value: "kn",    label: "Kannada" },
  { value: "en",    label: "English" },
  { value: "ko",    label: "Korean" },
  { value: "ja",    label: "Japanese" },
];

const DEFAULT_FILTERS = { type: "all", sort: "popular", lang: "all", rating: 0 };

export default function HomeWatchOn() {
  const [selected, setSelected] = useState(() => {
    try { return localStorage.getItem("fm_watch_on") || "netflix"; } catch { return "netflix"; }
  });
  const [byPlatform, setByPlatform] = useState(() => {
    const k = typeof window !== "undefined" ? (localStorage.getItem("fm_watch_on") || "netflix") : "netflix";
    const c = readSwrCache(`watch-on:${k}`);
    return c ? { [k]: c } : {};
  });
  const [logoUrls, setLogoUrls] = useState({});
  const [loading, setLoading]   = useState(false);
  const [filters, setFilters]   = useState(DEFAULT_FILTERS);
  const [totalByPlatform, setTotalByPlatform] = useState({});
  const scrollRef = useRef(null);

  // Pre-fetch all platform logos on mount from lightweight metadata endpoint
  useEffect(() => {
    api.get("/watch-on-providers").then(({ data }) => {
      const map = {};
      (data.providers || []).forEach((p) => { if (p.logo_url) map[p.key] = p.logo_url; });
      setLogoUrls(map);
    }).catch(() => {
      // fallback: lazy-load logos per platform
    });
  }, []);

  // Fetch content when platform changes
  useEffect(() => {
    let cancelled = false;
    async function go() {
      if (!byPlatform[selected]) {
        const cached = readSwrCache(`watch-on:${selected}`);
        if (cached?.length) setByPlatform((p) => ({ ...p, [selected]: cached }));
      }
      setLoading(true);
      try {
        const { data } = await api.get(`/watch-on?platform=${selected}&limit=50`);
        if (cancelled) return;
        const arr = data.results || [];
        setByPlatform((p) => ({ ...p, [selected]: arr }));
        if (data.logo_url) setLogoUrls((p) => ({ ...p, [selected]: data.logo_url }));
        if (data.total) setTotalByPlatform((p) => ({ ...p, [selected]: data.total }));
        if (arr.length) writeSwrCache(`watch-on:${selected}`, arr);
      } catch {
        if (!cancelled) setByPlatform((p) => ({ ...p, [selected]: p[selected] || [] }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    go();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => {
    try { localStorage.setItem("fm_watch_on", selected); } catch {}
    if (scrollRef.current) scrollRef.current.scrollTo({ left: 0, behavior: "smooth" });
    setFilters(DEFAULT_FILTERS); // reset filters on platform change
  }, [selected]);

  const rawItems = useMemo(() => byPlatform[selected] || [], [byPlatform, selected]);

  // Apply client-side filters
  const items = useMemo(() => {
    let out = [...rawItems];
    if (filters.type === "movie") out = out.filter((it) => it.media_type === "movie");
    else if (filters.type === "tv") out = out.filter((it) => it.media_type === "tv");
    if (filters.lang !== "all") out = out.filter((it) => it.original_language === filters.lang);
    if (filters.rating > 0) {
      out = out.filter((it) => {
        const score = it.imdb_rating || it.vote_average || 0;
        return score >= filters.rating;
      });
    }
    if (filters.sort === "latest") {
      out = [...out].sort((a, b) => {
        const da = (a.release_date || a.first_air_date || "").replace(/-/g, "");
        const db = (b.release_date || b.first_air_date || "").replace(/-/g, "");
        return db.localeCompare(da);
      });
    } else if (filters.sort === "oldest") {
      out = [...out].sort((a, b) => {
        const da = (a.release_date || a.first_air_date || "9999").replace(/-/g, "");
        const db = (b.release_date || b.first_air_date || "9999").replace(/-/g, "");
        return da.localeCompare(db);
      });
    } else if (filters.sort === "rating") {
      out = [...out].sort((a, b) => {
        const ra = a.imdb_rating || a.vote_average || 0;
        const rb = b.imdb_rating || b.vote_average || 0;
        return rb - ra;
      });
    }
    return out;
  }, [rawItems, filters]);

  const pillMeta = PILL_ORDER.find((p) => p.key === selected) || PILL_ORDER[0];

  const setFilter = useCallback((k, v) => setFilters((prev) => ({ ...prev, [k]: v })), []);

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };

  const hasFilters = filters.type !== "all" || filters.sort !== "popular" || filters.lang !== "all" || filters.rating > 0;

  return (
    <section className="px-6 lg:px-10 max-w-[1440px] mx-auto mt-12" data-testid="home-watch-on">
      {/* Section header */}
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-1 h-7 rounded-full shrink-0 bg-white/40" />
          <h2 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight flex items-center gap-2">
            <Tv2 className="w-5 h-5 text-white/70 shrink-0" />
            <span className="truncate">Watch On</span>
          </h2>
        </div>
        <Link
          to={`/platform/${selected}`}
          className="shrink-0 flex items-center gap-1 text-xs font-semibold text-white/50 hover:text-white transition-colors group"
          data-testid="watch-on-see-all"
        >
          See all
          <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>

      {/* Platform pills */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1 no-scrollbar" data-testid="watch-on-pills">
        {PILL_ORDER.map((p) => {
          const active      = p.key === selected;
          const logoSrc     = logoUrls[p.key];
          const subtleStyle = PLATFORM_STYLES[p.key] || PLATFORM_STYLES.other;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => setSelected(p.key)}
              aria-pressed={active}
              data-testid={`watch-on-pill-${p.key}`}
              className={`shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold border transition-all whitespace-nowrap ${
                active
                  ? `bg-white/10 border-white/20 text-white shadow-lg scale-[1.03] ring-2 ${p.ring}`
                  : `${subtleStyle.bg} ${subtleStyle.border} ${subtleStyle.text} hover:scale-[1.02] hover:bg-white/10`
              }`}
            >
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt={p.name}
                  className="w-5 h-5 rounded object-contain shrink-0"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <span className="w-5 h-5 rounded bg-white/10 shrink-0" />
              )}
              {p.name}
            </button>
          );
        })}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="watch-on-filters">
        {/* Type */}
        <div className="flex gap-1 bg-white/[0.04] rounded-lg p-0.5 border border-white/10">
          {[["all","All"],["movie","Movies"],["tv","TV"]].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setFilter("type", v)}
              data-testid={`filter-type-${v}`}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                filters.type === v ? "bg-white/15 text-white" : "text-white/40 hover:text-white/70"
              }`}
            >{l}</button>
          ))}
        </div>

        {/* Sort */}
        <div className="flex gap-1 bg-white/[0.04] rounded-lg p-0.5 border border-white/10">
          {[["popular","Popular"],["latest","Latest"],["oldest","Oldest"],["rating","Rating"]].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setFilter("sort", v)}
              data-testid={`filter-sort-${v}`}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                filters.sort === v ? "bg-white/15 text-white" : "text-white/40 hover:text-white/70"
              }`}
            >{l}</button>
          ))}
        </div>

        {/* Language */}
        <select
          value={filters.lang}
          onChange={(e) => setFilter("lang", e.target.value)}
          data-testid="filter-language"
          className="bg-white/[0.04] border border-white/10 text-white/70 text-xs font-semibold rounded-lg px-2.5 py-1.5 cursor-pointer hover:bg-white/10 transition-all outline-none"
        >
          {LANG_OPTIONS.map(({ value, label }) => (
            <option key={value} value={value} className="bg-neutral-900">{label}</option>
          ))}
        </select>

        {/* Rating */}
        <div className="flex gap-1">
          {[[0,"Any"],[6,"6+"],[7,"7+"],[8,"8+"]].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setFilter("rating", v)}
              data-testid={`filter-rating-${v}`}
              className={`px-2.5 py-1 rounded-lg text-xs font-bold border transition-all ${
                filters.rating === v
                  ? "bg-amber-500/20 border-amber-500/50 text-amber-400"
                  : "bg-white/[0.04] border-white/10 text-white/40 hover:text-white/70"
              }`}
            >{l}</button>
          ))}
        </div>

        {/* Clear filters */}
        {hasFilters && (
          <button
            onClick={() => setFilters(DEFAULT_FILTERS)}
            className="text-xs text-white/30 hover:text-white/60 transition-colors underline underline-offset-2"
            data-testid="filter-clear"
          >
            Clear
          </button>
        )}

        {/* Result count */}
        {!loading && (
          <span className="ml-auto text-xs text-white/25 shrink-0">
            {items.length} title{items.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Horizontal poster rail */}
      <div className="relative group">
        <button
          type="button"
          onClick={() => scrollBy(-1)}
          className="hidden sm:flex absolute -left-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-black/70 border border-white/15 items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black"
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
        <button
          type="button"
          onClick={() => scrollBy(1)}
          className="hidden sm:flex absolute -right-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-black/70 border border-white/15 items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black"
          aria-label="Scroll right"
        >
          <ChevronRight className="w-5 h-5 text-white" />
        </button>

        <div
          ref={scrollRef}
          className="flex gap-3 sm:gap-4 overflow-x-auto pb-4 snap-x snap-mandatory no-scrollbar"
          data-testid="watch-on-rail"
        >
          {loading && items.length === 0 ? (
            Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="shrink-0 w-36 sm:w-44 snap-start">
                <div className="aspect-[2/3] rounded-lg bg-white/[0.04] animate-pulse" />
                <div className="h-3.5 mt-2 w-3/4 bg-white/[0.04] rounded animate-pulse" />
                <div className="h-2.5 mt-1.5 w-1/2 bg-white/[0.04] rounded animate-pulse" />
              </div>
            ))
          ) : items.length === 0 ? (
            <div className="text-sm text-white/50 py-12 px-4">
              {hasFilters
                ? "No titles match your current filters. Try adjusting them."
                : `No popular titles found on ${pillMeta.name} right now.`}
            </div>
          ) : (
            <>
              {items.map((it) => (
                <div key={`${it.media_type}-${it.tmdb_id}`} className="snap-start">
                  <MovieCard item={it} showPlatform={false} />
                </div>
              ))}
              {/* Ghost "See more" card — always last in rail */}
              {!hasFilters && (
                <Link
                  to={`/platform/${selected}`}
                  data-testid="watch-on-see-more-card"
                  className="snap-start shrink-0 w-36 sm:w-44 group"
                >
                  <div className="aspect-[2/3] rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] transition-all flex flex-col items-center justify-center gap-3">
                    <div className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center group-hover:border-white/50 transition-colors">
                      <Layers className="w-5 h-5 text-white/40 group-hover:text-white/80 transition-colors" />
                    </div>
                    <div className="text-center px-2">
                      {totalByPlatform[selected] > items.length ? (
                        <span className="text-xs font-bold text-white/50 group-hover:text-white/80 transition-colors">
                          +{totalByPlatform[selected] - items.length} more
                        </span>
                      ) : (
                        <span className="text-xs font-bold text-white/50 group-hover:text-white/80 transition-colors">
                          See all
                        </span>
                      )}
                      <p className="text-[10px] text-white/25 mt-0.5 group-hover:text-white/40 transition-colors">
                        {pillMeta.name}
                      </p>
                    </div>
                  </div>
                </Link>
              )}
            </>
          )}
        </div>
      </div>

      {/* See All link (bottom) */}
      <div className="mt-2 text-center">
        <Link
          to={`/platform/${selected}`}
          className="inline-flex items-center gap-1.5 text-xs text-white/30 hover:text-white/60 transition-colors group"
        >
          See all {pillMeta.name} titles
          <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>
    </section>
  );
}
